import { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import RuleSummary from "./RuleSummary";
import TruckTypesDisplay from "./TruckTypesDisplay";
import { MoreOptionsIcon } from "components/assets/Icons";
import useOnClickOutside from "utils/useOnClickOutside";

import styles from "./RulesList.module.scss";

const EMPTY_PLACEHOLDER = "—";

// Renders the "Email Recipients" cell showing the rule's effective recipients.
// At most `maxVisible` are shown; the rest collapse into `+N` (N = total −
// shown). The full effective list is exposed via the cell title (hover).
// Invariant: shown + N === total.
const RecipientsCell = ({ recipients, maxVisible }) => {
  const effective = Array.isArray(recipients) ? recipients : [];

  if (effective.length === 0) {
    return <span className={styles.recipientText}>{EMPTY_PLACEHOLDER}</span>;
  }

  const shown = effective.slice(0, maxVisible);
  const hiddenCount = effective.length - shown.length;
  const fullList = effective.join(", ");

  return (
    <span className={styles.recipientText} title={fullList}>
      {shown.join(", ")}
      {hiddenCount > 0 && (
        <span className={styles.recipientMore}> +{hiddenCount}</span>
      )}
    </span>
  );
};

const RuleRow = ({ rule, onEdit, onDelete, onTest, maxVisible = 1 }) => {
  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef();
  const dispatch = useDispatch();

  useOnClickOutside(optionsRef, () => setShowOptions(false));

  const getStatusBadge = () => {
    return (
      <span className={`${styles.statusBadge} ${rule.is_active ? styles.active : styles.inactive}`}>
        {rule.is_active ? "Active" : "Inactive"}
      </span>
    );
  };

  const handleEdit = () => {
    onEdit(rule);
    setShowOptions(false);
  };

  const handleTest = () => {
    onTest(rule.id);
    setShowOptions(false);
  };

  const handleDelete = () => {
    onDelete(rule.id);
    setShowOptions(false);
  };

  return (
    <div className={styles.row}>
      <div className={styles.nameCell}>
        <div className={styles.ruleName}>
          {rule.name || `Rule #${rule.id}`}
        </div>
      </div>
      <div className={styles.criteriaCell}>
        <RuleSummary criteria={rule} />
      </div>
      <div className={styles.truckTypesCell}>
        <TruckTypesDisplay 
          allTruckTypes={rule.all_truck_types} 
          allowedTruckTypes={rule.allowed_truck_types} 
        />
      </div>
      <div className={styles.contactsCell}>
        <span className={styles.contactCount}>
          {rule.customers_count || 0} customers, {rule.customer_contacts_count || 0} contacts
        </span>
      </div>
      <div className={styles.statusCell}>
        {getStatusBadge()}
      </div>
      <div className={styles.createdByCell}>
        <span className={styles.metaText}>
          {rule.created_by_name || rule.employee_name || EMPTY_PLACEHOLDER}
        </span>
      </div>
      <div className={styles.createdTimeCell}>
        <span className={styles.metaText}>
          {rule.created_time || EMPTY_PLACEHOLDER}
        </span>
      </div>
      <div className={styles.lastEditedByCell}>
        <span className={styles.metaText}>
          {rule.last_edited_by_name || EMPTY_PLACEHOLDER}
        </span>
      </div>
      <div className={styles.lastEditedTimeCell}>
        <span className={styles.metaText}>
          {rule.last_edited_time || EMPTY_PLACEHOLDER}
        </span>
      </div>
      <div className={styles.recipientsCell}>
        <RecipientsCell recipients={rule.email_recipients} maxVisible={maxVisible} />
      </div>
      <div className={styles.actionsCell}>
        <div className={styles.actionsContainer}>
          <button
            className={styles.optionsBtn}
            onClick={() => setShowOptions(!showOptions)}
          >
            <MoreOptionsIcon />
          </button>
          {showOptions && (
            <div className={styles.options} ref={optionsRef}>
              {/* Edit/Delete only when the user may modify this rule (own rule
                  or manage-all); mirrors the backend write guard via can_edit.
                  Test stays available to view-all users. */}
              {rule.can_edit && (
                <button
                  className={styles.optionsItem}
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
              <button
                className={styles.optionsItem}
                onClick={handleTest}
              >
                Test
              </button>
              {rule.can_edit && (
                <button
                  className={`${styles.optionsItem} ${styles.deleteButton}`}
                  onClick={handleDelete}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleRow;